import type { Theme } from '@mui/material/styles'
import type { SystemStyleObject } from '@mui/system'
import { alpha } from '@mui/material/styles'
import { Instance as TinyColor } from 'tinycolor2'
import { makeStyles } from 'tss-react/mui'
import { darkBg } from '../../../assets/colors'

export const COLOR_SELECTOR_SLIDER_WIDTH = 150
export const SLIDER_CHROME_HEIGHT = 10
/** Half of chrome height — fully rounded end caps on the track border. */
export const SLIDER_CHROME_RADIUS = `${SLIDER_CHROME_HEIGHT / 2}px`
export const SLIDER_THUMB_SIZE = 8

/** Outer chrome: single 1px primary border; fill lives inside with no gap at the leading edge. */
export const getSliderChromeBoxSx = (): SystemStyleObject => ({
    width: COLOR_SELECTOR_SLIDER_WIDTH,
    height: SLIDER_CHROME_HEIGHT,
    boxSizing: 'border-box',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'primary.main',
    borderRadius: SLIDER_CHROME_RADIUS,
    overflow: 'hidden',
    flexShrink: 0,
    position: 'relative',
})

const sliderThumbStyles = (theme: Theme) => ({
    width: `${SLIDER_THUMB_SIZE}px !important`,
    height: `${SLIDER_THUMB_SIZE}px !important`,
    minWidth: `${SLIDER_THUMB_SIZE}px !important`,
    minHeight: `${SLIDER_THUMB_SIZE}px !important`,
    boxSizing: 'border-box' as const,
    backgroundColor: theme.palette.mode === 'dark' ? darkBg : '#fff',
    boxShadow: 'none !important',
    borderRadius: '50% !important',
    border: `1px solid ${theme.palette.mode === 'dark' ? '#fff' : darkBg}`,

    '&::before': {
        display: 'none',
    },
    '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
        boxShadow: 'none !important',
    },
})

const sliderValueLabelStyles = (theme: Theme) => ({
    fontSize: 12,
    fontWeight: 'normal',
    top: -6,
    backgroundColor: 'unset',
    color: theme.palette.text.primary,
    '&::before': {
        display: 'none',
    },
    '& *': {
        background: 'transparent',
        color: theme.palette.text.primary,
    },
})

const sliderRailLayout = {
    opacity: 1,
    height: '100%',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    width: '100%',
    border: 'none',
    borderRadius: 0,
    boxSizing: 'border-box' as const,
    transform: 'none',
}

const sliderTrackLayout = {
    height: '100%',
    top: 0,
    bottom: 0,
    border: 'none',
    boxSizing: 'border-box' as const,
    transform: 'none',
    borderRadius: 0,
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
}

const sliderRootLayout = {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
    padding: '0 !important',
    margin: 0,
    boxSizing: 'border-box' as const,

    '&.MuiSlider-sizeSmall': {
        padding: '0 !important',
    },
}

const settingsSliderTrackColor = (theme: Theme) => alpha(theme.palette.text.primary, 0.38)

export const useOpacitySliderStyles = makeStyles<{ color: TinyColor }>()((theme, params) => ({
    root: {
        color: '#007bff',
        ...sliderRootLayout,

        '& .MuiSlider-thumb': sliderThumbStyles(theme),
        '& .MuiSlider-valueLabel': sliderValueLabelStyles(theme),
        '& .MuiSlider-track': {
            ...sliderTrackLayout,
            background: 'none',
        },
        '& .MuiSlider-rail': {
            ...sliderRailLayout,
            background: `linear-gradient(90deg, transparent, ${params.color.toRgbString()})`,
        },
    },
}))

export const useSettingsSliderStyles = makeStyles()((theme) => ({
    root: {
        color: settingsSliderTrackColor(theme),
        ...sliderRootLayout,

        '& .MuiSlider-thumb': sliderThumbStyles(theme),
        '& .MuiSlider-valueLabel': sliderValueLabelStyles(theme),
        '& .MuiSlider-track': {
            ...sliderTrackLayout,
            borderTopLeftRadius: SLIDER_CHROME_RADIUS,
            borderBottomLeftRadius: SLIDER_CHROME_RADIUS,
            backgroundColor: settingsSliderTrackColor(theme),
        },
        '& .MuiSlider-rail': {
            ...sliderRailLayout,
            background: 'transparent',
        },
    },
}))
